import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { Product } from '../data/menuData.ts';
import LazyImage from './LazyImage.tsx';
import { SHOW_MENU_ORDERING, SHOW_MENU_PRICES } from '../config/ui.ts';

interface ProductModalProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (product: Product, options: any, quantity: number) => void;
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  overflow-y: auto;
`;

const ModalContent = styled.div`
  background: var(--white);
  border-radius: 20px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    max-width: 95%;
    max-height: 95vh;
    border-radius: 15px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  color: var(--text-dark);
  transition: all 0.3s ease;
  z-index: 10;
  
  &:hover {
    background: rgba(0, 0, 0, 0.2);
    transform: scale(1.1);
  }
`;

const ProductImage = styled.div`
  height: 300px;
  background: linear-gradient(45deg, rgba(0, 191, 210, 0.1), rgba(1, 216, 204, 0.1)),
              linear-gradient(135deg, var(--primary-turquoise), var(--light-turquoise));
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: var(--navy-blue);
  border-radius: 20px 20px 0 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 20px 20px 0 0;
  }
  
  @media (max-width: 768px) {
    height: 200px;
    font-size: 3rem;
    border-radius: 15px 15px 0 0;
  }
`;

const ProductInfo = styled.div`
  padding: 30px;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const ProductHeader = styled.div`
  margin-bottom: 20px;
`;

const ProductName = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 10px;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ProductPrice = styled.div`
  font-size: 1.6rem;
  font-weight: 800;
  color: var(--primary-turquoise);
  margin-bottom: 15px;
`;

const SpicyLevel = styled.div<{ level: 'mild' | 'medium' | 'hot' }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: ${props => 
    props.level === 'hot' ? 'rgba(255, 87, 87, 0.1)' :
    props.level === 'medium' ? 'rgba(255, 193, 87, 0.1)' :
    'rgba(87, 255, 87, 0.1)'
  };
  color: ${props => 
    props.level === 'hot' ? '#FF5757' :
    props.level === 'medium' ? '#FFC157' :
    '#57FF57'
  };
  padding: 5px 12px;
  border-radius: 15px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 15px;
  
  i {
    font-size: 0.8rem;
  }
`;

const ProductDescription = styled.p`
  font-size: 1rem;
  color: var(--text-dark);
  line-height: 1.6;
  margin-bottom: 20px;
  opacity: 0.8;
`;

const IngredientsSection = styled.div`
  margin-bottom: 25px;
`;

const IngredientsTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 10px;
`;

const IngredientsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const IngredientTag = styled.span`
  background: rgba(0, 191, 210, 0.1);
  color: var(--primary-turquoise);
  padding: 5px 12px;
  border-radius: 15px;
  font-size: 0.85rem;
  font-weight: 500;
`;

const OptionsSection = styled.div`
  margin-bottom: 25px;
`;


const OptionTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 12px;
`;

const OptionChoice = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: 2px solid rgba(0, 191, 210, 0.2);
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--primary-turquoise);
    background: rgba(0, 191, 210, 0.05);
  }
  
  input {
    margin-right: 12px;
  }
`;

const ChoiceName = styled.span`
  font-weight: 500;
  color: var(--text-dark);
`;

const ChoicePrice = styled.span`
  font-weight: 600;
  color: var(--primary-turquoise);
`;

const QuantitySection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(0, 191, 210, 0.05);
  border-radius: 15px;
`;

const QuantityLabel = styled.span`
  font-weight: 600;
  color: var(--text-dark);
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const QuantityButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid var(--primary-turquoise);
  background: var(--white);
  color: var(--primary-turquoise);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: 600;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--primary-turquoise);
    color: var(--white);
    transform: scale(1.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background: var(--white);
      color: var(--primary-turquoise);
      transform: none;
    }
  }
`;

const QuantityDisplay = styled.span`
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text-dark);
  min-width: 30px;
  text-align: center;
`;

const AddToCartButton = styled.button`
  width: 100%;
  background: var(--gradient-primary);
  color: var(--navy-blue);
  border: none;
  padding: 15px 20px;
  border-radius: 15px;
  font-size: 1.1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 191, 210, 0.3);
  }
  
  i {
    font-size: 1rem;
  }
`;

const ProductModal: React.FC<ProductModalProps> = ({
  product,
  isOpen,
  onClose,
  onAddToCart
}) => {
  const [selectedOptions, setSelectedOptions] = useState<any>({});
  const [selectedSize, setSelectedSize] = useState<any>(null);
  const [quantity, setQuantity] = useState(1);
  const [totalPrice, setTotalPrice] = useState(0);

  useEffect(() => {
    if (isOpen && product) {
      // Reset selections
      setSelectedOptions({});
      setSelectedSize(product.sizes ? product.sizes[0] : null);
      setQuantity(1);
      
      // Animate modal entrance
      gsap.fromTo('.modal-content',
        { opacity: 0, scale: 0.8, y: 50 },
        { opacity: 1, scale: 1, y: 0, duration: 0.4, ease: 'back.out(1.7)' }
      );
    }
  }, [isOpen, product]);

  useEffect(() => {
    if (!product) return;
    
    let price = selectedSize ? selectedSize.price : product.price;
    
    // Add option prices
    Object.values(selectedOptions).forEach((option: any) => {
      if (option && option.price) {
        price += option.price;
      }
    });
    
    setTotalPrice(price * quantity);
  }, [product, selectedSize, selectedOptions, quantity]);

  const handleOptionChange = (optionName: string, choice: any) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionName]: choice
    }));
  };

  const handleQuantityChange = (delta: number) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= 10) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    const cartItem = {
      ...product,
      selectedSize,
      selectedOptions,
      totalPrice: totalPrice / quantity
    };
    
    onAddToCart(cartItem, selectedOptions, quantity);
    onClose();
  };

  const getSpicyLevelIcon = (level: string) => {
    switch (level) {
      case 'hot': return '🌶️🌶️🌶️';
      case 'medium': return '🌶️🌶️';
      case 'mild': return '🌶️';
      default: return '';
    }
  };

  if (!product) return null;

  return (
    <ModalOverlay isOpen={isOpen} onClick={onClose}>
      <ModalContent className="modal-content" onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <i className="fas fa-times"></i>
        </CloseButton>
        
        <ProductImage>
          <LazyImage
            src={product.image}
            fallbackSrc={`${process.env.PUBLIC_URL}/logo.png`}
            alt={product.name}
            width="100%"
            height="100%"
            objectFit="cover"
            placeholder="fas fa-image"
          />
        </ProductImage>
        
        <ProductInfo>
          <ProductHeader>
            <ProductName>{product.name}</ProductName>
            {SHOW_MENU_PRICES && (
              <ProductPrice>S/. {(selectedSize?.price || product.price).toFixed(2)}</ProductPrice>
            )}
            {product.spicyLevel && (
              <SpicyLevel level={product.spicyLevel}>
                <span>{getSpicyLevelIcon(product.spicyLevel)}</span>
                <span>
                  {product.spicyLevel === 'hot' ? 'Picante' :
                   product.spicyLevel === 'medium' ? 'Medio' : 'Suave'}
                </span>
              </SpicyLevel>
            )}
          </ProductHeader>
          
          <ProductDescription>{product.description}</ProductDescription>
          
          <IngredientsSection>
            <IngredientsTitle>Ingredientes:</IngredientsTitle>
            <IngredientsList>
              {product.ingredients.map((ingredient, index) => (
                <IngredientTag key={index}>{ingredient}</IngredientTag>
              ))}
            </IngredientsList>
          </IngredientsSection>
          
          {product.sizes && (
            <OptionsSection>
              <OptionTitle>Tamaño:</OptionTitle>
              {product.sizes.map((size, index) => (
                <OptionChoice key={index}>
                  <input
                    type="radio"
                    name="size"
                    checked={selectedSize?.name === size.name}
                    onChange={() => setSelectedSize(size)}
                  />
                  <ChoiceName>{size.name}</ChoiceName>
                  {SHOW_MENU_PRICES && (
                    <ChoicePrice>S/. {size.price.toFixed(2)}</ChoicePrice>
                  )}
                </OptionChoice>
              ))}
            </OptionsSection>
          )}
          
          {product.options?.map((option, optionIndex) => (
            <OptionsSection key={optionIndex}>
              <OptionTitle>{option.name}:</OptionTitle>
              {option.choices.map((choice, choiceIndex) => (
                <OptionChoice key={choiceIndex}>
                  <input
                    type="radio"
                    name={option.name}
                    checked={selectedOptions[option.name]?.name === choice.name}
                    onChange={() => handleOptionChange(option.name, choice)}
                  />
                  <ChoiceName>{choice.name}</ChoiceName>
                  {SHOW_MENU_PRICES && choice.price > 0 && (
                    <ChoicePrice>+S/. {choice.price.toFixed(2)}</ChoicePrice>
                  )}
                </OptionChoice>
              ))}
            </OptionsSection>
          ))}
          
          {SHOW_MENU_ORDERING && (
            <>
              <QuantitySection>
                <QuantityLabel>Cantidad:</QuantityLabel>
                <QuantityControls>
                  <QuantityButton
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                  >
                    <i className="fas fa-minus"></i>
                  </QuantityButton>
                  <QuantityDisplay>{quantity}</QuantityDisplay>
                  <QuantityButton
                    onClick={() => handleQuantityChange(1)}
                    disabled={quantity >= 10}
                  >
                    <i className="fas fa-plus"></i>
                  </QuantityButton>
                </QuantityControls>
              </QuantitySection>
              
              <AddToCartButton onClick={handleAddToCart}>
                <i className="fas fa-cart-plus"></i>
                {SHOW_MENU_PRICES ? `Agregar S/. ${totalPrice.toFixed(2)}` : 'Agregar'}
              </AddToCartButton>
            </>
          )}
        </ProductInfo>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ProductModal;